import React, { useMemo } from "react";

// GitHub-style contribution heatmap for articles created per day.
export default function ActivityHeatmap({ data }) {
  const { weeks, total, monthLabels } = useMemo(() => {
    const days = (data?.days || []);
    if (!days.length) return { weeks: [], total: 0, monthLabels: [] };
    const map = {};
    days.forEach((d) => { map[d.date] = d.count; });

    // Align start to the Sunday on/ before the first date
    const first = new Date(days[0].date + "T00:00:00");
    const startOffset = first.getDay(); // 0 = Sunday
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null); // pad
    days.forEach((d) => cells.push(d));

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    const monthLabels = [];
    weeks.forEach((w, wi) => {
      const firstReal = w.find(Boolean);
      if (!firstReal) return;
      const dt = new Date(firstReal.date + "T00:00:00");
      const m = dt.toLocaleString("en", { month: "short" });
      const prev = monthLabels[monthLabels.length - 1];
      if (!prev || prev.label !== m) monthLabels.push({ index: wi, label: m });
    });

    return { weeks, total: data?.total || 0, monthLabels };
  }, [data]);

  const level = (c) => {
    if (!c) return "bg-pink-100";
    if (c === 1) return "bg-pink-300";
    if (c === 2) return "bg-pink-400";
    if (c <= 4) return "bg-rose-500";
    return "bg-rose-600";
  };

  if (!weeks.length) return null;

  return (
    <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-5" data-testid="manchi-heatmap">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold text-rose-900">Your writing activity</div>
        <div className="text-xs text-rose-400" data-testid="manchi-heatmap-total">{total} article{total === 1 ? "" : "s"} this year</div>
      </div>
      <div className="overflow-x-auto pb-1">
        <div className="inline-flex flex-col gap-1 min-w-max">
          <div className="flex gap-[3px] ml-[26px] mb-1">
            {weeks.map((_, wi) => {
              const lbl = monthLabels.find((m) => m.index === wi);
              return <div key={wi} className="w-[11px] text-[9px] text-rose-300">{lbl ? lbl.label : ""}</div>;
            })}
          </div>
          <div className="flex gap-[3px]">
            <div className="flex flex-col gap-[3px] mr-1 text-[9px] text-rose-300 justify-around">
              <span>Mon</span><span>Wed</span><span>Fri</span>
            </div>
            <div className="flex gap-[3px]">
              {weeks.map((w, wi) => (
                <div key={wi} className="flex flex-col gap-[3px]">
                  {Array.from({ length: 7 }).map((_, di) => {
                    const cell = w[di];
                    return (
                      <div key={di}
                        title={cell ? `${cell.date}: ${cell.count} article${cell.count === 1 ? "" : "s"}` : ""}
                        className={`w-[11px] h-[11px] rounded-[3px] ${cell ? level(cell.count) : "bg-transparent"}`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5 justify-end mt-2 text-[10px] text-rose-300">
            Less
            <span className="w-[11px] h-[11px] rounded-[3px] bg-pink-100" />
            <span className="w-[11px] h-[11px] rounded-[3px] bg-pink-300" />
            <span className="w-[11px] h-[11px] rounded-[3px] bg-pink-400" />
            <span className="w-[11px] h-[11px] rounded-[3px] bg-rose-500" />
            <span className="w-[11px] h-[11px] rounded-[3px] bg-rose-600" />
            More
          </div>
        </div>
      </div>
    </div>
  );
}
